/* eslint-disable no-dupe-class-members */
/**
 * `HttpEndpoints` provides static utilities for implementing a set of
 * API endpoints which returns requests that can be passed to `api.request`.
 */
export class HttpEndpoints {
    /**
     * Returns a new path by appending a given `relativePath` to `this.basePath`.
     */
    static buildPath(relativePath) {
        let builtPath = `${this.basePath}${relativePath}`;
        if (this.trailingSlash && builtPath[builtPath.length - 1] !== '/') {
            builtPath += '/';
        }
        return builtPath;
    }
    /**
     * Creates a `GET` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _get(path, requestInit) {
        return this._createRequest('GET', path, requestInit);
    }
    /**
     * Creates a `POST` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _post(path, requestInit) {
        return this._createRequest('POST', path, requestInit);
    }
    /**
     * Creates a `PUT` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _put(path, requestInit) {
        return this._createRequest('PUT', path, requestInit);
    }
    /**
     * Creates a `PATCH` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _patch(path, requestInit) {
        return this._createRequest('PATCH', path, requestInit);
    }
    /**
     * Creates a `DELETE` request.
     *
     * @param path A path relative to the base path.
     * @param requestInit Additional request parameters.
     */
    static _delete(path, requestInit) {
        return this._createRequest('DELETE', path, requestInit);
    }
    /**
     * Given an object of key-value pairs, will serialize the query parameters into a search string.
     * This is called internally when serializing the query parameters into the request `url`.
     *
     * To customize query parameter serialization, override this in your base class.
     */
    static _serializeQuery(query) {
        const queryWithoutEmptyValues = Object.entries(query).reduce((prev, [key, value]) => {
            if (value !== undefined) {
                prev[key] = value;
            }
            return prev;
        }, {});
        const params = new URLSearchParams(queryWithoutEmptyValues);
        params.sort();
        return params.toString();
    }
    static _createRequest(method, path, requestInit = {}) {
        let url = this.buildPath(path);
        if (requestInit.query) {
            url += `?${this._serializeQuery(requestInit.query)}`;
        }
        return {
            method,
            url,
            headers: requestInit.headers,
            body: requestInit.body,
            extraKey: requestInit.extraKey
        };
    }
}
/**
 * The base path to prefix all underlying requests.
 */
HttpEndpoints.basePath = '';
/**
 * Some REST APIs require that requests end with a trailing slash. (ie. `/users/` instead of `/users`)
 * If `true`, will ensure that a trailing `/` is added to each request.
 */
HttpEndpoints.trailingSlash = false;
export class RestEndpoints extends HttpEndpoints {
    static _singlePath(id) {
        return dynamicPath('/:id', { id });
    }
    /**
     * Returns the path for a given resource id, as a convenience.
     */
    static pathToResource(id) {
        return super.buildPath(this._singlePath(id));
    }
    /**
     * Performs a `GET` at `/`, appending an optional `query`
     * @param query Query parameters
     */
    static _list(query) {
        return super._get(this._collectionPath, { query });
    }
    /**
     * Performs a `POST` at `/`, using an optional request `body`
     */
    static _create(body) {
        return super._post(this._collectionPath, { body });
    }
    /**
     * Performs a `GET` at `/:id`
     */
    static _findById(id) {
        return super._get(this._singlePath(id));
    }
    /**
     * Performs a `PUT` at `/:id`, using an optional request `body`
     */
    static _update(id, body) {
        return super._put(this._singlePath(id), { body });
    }
    /**
     * Performs a `PATCH` at `/:id`, using an optional request `body`
     */
    static _partialUpdate(id, body) {
        return super._patch(this._singlePath(id), { body });
    }
    /**
     * Performs a `DELETE` at `/:id`, using an optional request `body`
     */
    static _destroy(id, body) {
        return super._delete(this._singlePath(id), { body });
    }
}
RestEndpoints._collectionPath = '';
/**
 * Applies colon-delimited path params to a given path pattern
 *
 * @param pattern Path pattern
 * @param params Path params
 *
 * @example `buildPath('/users/:id', { id: 4 })` => `/users/4`
 */
export function dynamicPath(pattern, params) {
    return Object.entries(params).reduce((prev, [paramKey, paramValue]) => {
        return prev.replace(`:${paramKey}`, encodeURIComponent(String(paramValue)));
    }, pattern);
}
//# sourceMappingURL=index.js.map